import { Info, Check, ClipboardX, Inbox } from 'lucide-react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Fragment, useMemo } from 'react';
import { useSuspenseQuery } from '@apollo/client/react';

import { withSuspense } from 'src/hoc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableHeader, TableHead, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { IconBadgeCertified, IconBadgeVerified, IconBadgeValidated } from 'src/components/Svg/Images';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AssetContentTypeEnum, EscrowStatusEnum } from 'src/graphql/schema';
import { AssetsDocument } from 'src/graphql/actions/asset/assets.query';
import { CertificateContainer } from 'src/containers/SoftwareEscrow/Certificate';
import { contentTypeMap } from 'src/containers/SoftwareEscrow/Deposits';
import { RequestCertifiedAction, GetAddOnAction } from 'src/containers/SoftwareEscrow/Reports';
import { EmptyBlock } from '@/components/ui/empty-block';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from 'src/lib/utils';
import { CertificatePlaceholder } from 'src/components/Certificate/Placeholder';

const CertificatePreview = ({
  type,
  isTerminated = false,
  isEnabled = false,
}: {
  type: 'validated' | 'verified' | 'certified';
  isTerminated?: boolean;
  isEnabled?: boolean;
}) => {
  return (
    <div className="inline-flex mx-auto relative">
      {isTerminated && isEnabled && (
        <span className="uppercase text-sm text-muted-foreground absolute top-[50%] left-[50%] -translate-1/2 border-2 border-muted-foreground px-2 rounded-sm font-semibold -rotate-50 opacity-80">
          terminated
        </span>
      )}
      <CertificatePlaceholder
        type={type}
        className={cn({
          'opacity-30': !isEnabled,
          'bg-gray-50': isTerminated,
        })}
        badgeClassName={isTerminated ? 'opacity-30' : ''}
      />
    </div>
  );
};

const VerifiedCell = () => {
  const {
    softwareEscrow: { status, certificationVerified, reportsEnabled, policy },
  } = useSoftwareEscrow();

  const onLearnMore = () => window.open('https://codekeeper.co/verification', '_blank');

  const renderAction = () => {
    if (reportsEnabled && !certificationVerified)
      return (
        <Button variant="ghost" size="sm" className="w-full pointer-events-none font-normal">
          <Check className="text-accent-saas-escrow" />
          Acquired
        </Button>
      );

    if (policy.enableReports)
      return (
        <GetAddOnAction
          triggerNode={
            <Button size="sm" variant="secondary" className="w-full">
              Get Add-on
            </Button>
          }
        />
      );

    return null;
  };

  const isTerminated = status === EscrowStatusEnum.Terminated;
  const shouldShowVerificationCtas = (!reportsEnabled || !certificationVerified) && !isTerminated;

  return (
    <div className="space-y-8 max-w-44 mx-auto">
      <div className="p-6">
        <div className="relative max-w-[124px] flex mx-auto">
          <CertificatePreview type="verified" isTerminated={isTerminated} isEnabled={certificationVerified} />
          {!certificationVerified && (
            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
              <p className="text-xs text-foreground text-center">
                Verify the accuracy and relevance of the material placed in escrow.
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-4">
        {certificationVerified && (
          <CertificateContainer
            type="verified"
            dialogTrigger={
              <Button size="sm" className="w-full">
                View certificate
              </Button>
            }
            canDownload={policy.read}
          />
        )}
        {shouldShowVerificationCtas && (
          <Fragment>
            {renderAction()}
            <Button size="sm" variant="outline" className="w-full" onClick={onLearnMore}>
              Learn more
            </Button>
          </Fragment>
        )}
      </div>
    </div>
  );
};

const CertifiedCell = () => {
  const {
    softwareEscrow: { status, certificationCertified, policy },
  } = useSoftwareEscrow();

  const onLearnMore = () => window.open('https://codekeeper.co/verification', '_blank');

  const isTerminated = status === EscrowStatusEnum.Terminated;

  return (
    <div className="space-y-8 max-w-44 mx-auto">
      <div className="p-6">
        <div className="relative max-w-[124px] flex mx-auto">
          <CertificatePreview type="certified" isTerminated={isTerminated} isEnabled={certificationCertified} />
          {!certificationCertified && (
            <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
              <p className="text-xs text-foreground text-center">
                In-depth human analyses that give you complete assurance of continuity.
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-4">
        {certificationCertified && (
          <CertificateContainer
            type="certified"
            dialogTrigger={
              <Button size="sm" className="w-full">
                View certificate
              </Button>
            }
            canDownload={policy.read}
          />
        )}
        {!certificationCertified && !isTerminated && (
          <Fragment>
            <RequestCertifiedAction buttonText="Request certified" />
            <Button size="sm" variant="outline" className="w-full" onClick={onLearnMore}>
              Learn more
            </Button>
          </Fragment>
        )}
      </div>
    </div>
  );
};

type AssetSummaryCellProps = {
  isMarkedAsRequired: boolean;
  isEmpty: boolean;
  isEnabled: boolean;
  icon: React.ElementType;
};

const AssetSummaryCell = ({ isMarkedAsRequired, isEmpty, isEnabled, icon: Icon }: AssetSummaryCellProps) => {
  if (!isMarkedAsRequired) {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <div className="flex items-center justify-center size-5 bg-gray-200 border border-gray-500 rounded-full shadow-sm">
          <ClipboardX className="size-3 text-gray-500" />
        </div>
        <div className="text-xs text-gray-400">(Marked as not required)</div>
      </div>
    );
  }

  if (isEmpty) return <div className="text-sm text-center text-gray-500">0 assets</div>;

  if (isEnabled) {
    return (
      <div className="relative size-5 mx-auto">
        <Icon className="size-5" />
        <div className="absolute -top-[3px] -right-[5px] flex items-center justify-center size-2.5 bg-green-100 border border-green-500 rounded-full shadow-sm">
          <Check className="size-[7.5px] text-green-800" />
        </div>
      </div>
    );
  }

  return <Icon className="size-5 mx-auto opacity-30" />;
};

type ITableRow = {
  id: AssetContentTypeEnum;
  contentType: string;
  validated: {
    isMarkedAsRequired: boolean;
    isEnabled: boolean;
    isEmpty: boolean;
  };
  verified: {
    isMarkedAsRequired: boolean;
    isEnabled: boolean;
    isEmpty: boolean;
  };
  certified: {
    isMarkedAsRequired: boolean;
    isEnabled: boolean;
    isEmpty: boolean;
  };
};

const columnHelper = createColumnHelper<ITableRow>();
const columns = [
  columnHelper.accessor('contentType', {
    header: () => (
      <div className="flex items-center gap-1.5">
        Asset type
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="size-3.5 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[249px] bg-white text-sm text-popover-foreground shadow-md">
              If an asset type is not applicable to your app’s functionality, you can mark it as Not Required in the
              Deposits tab.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    cell: (props) => <span className="font-semibold">{props.getValue()}</span>,
  }),
  columnHelper.accessor('validated', {
    header: () => <span className="text-center block">Validated</span>,
    cell: (props) => <AssetSummaryCell {...props.getValue()} icon={IconBadgeValidated} />,
  }),
  columnHelper.accessor('verified', {
    header: () => <span className="text-center block">Verified</span>,
    cell: (props) => <AssetSummaryCell {...props.getValue()} icon={IconBadgeVerified} />,
  }),
  columnHelper.accessor('certified', {
    header: () => <span className="text-center block">Certified</span>,
    cell: (props) => <AssetSummaryCell {...props.getValue()} icon={IconBadgeCertified} />,
  }),
];

const CertificationTableSkeleton = () => {
  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <div className="flex items-center gap-1.5">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-3.5 w-3.5 rounded-full" />
              </div>
            </TableHead>
            <TableHead>
              <Skeleton className="h-4 w-16 mx-auto" />
            </TableHead>
            <TableHead>
              <Skeleton className="h-4 w-16 mx-auto" />
            </TableHead>
            <TableHead>
              <Skeleton className="h-4 w-16 mx-auto" />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Asset type rows */}
          {[1, 2, 3].map((i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-4 w-32" />
              </TableCell>
              <TableCell>
                <div className="flex justify-center">
                  <Skeleton className="h-5 w-5 rounded-full" />
                </div>
              </TableCell>
              <TableCell>
                <div className="flex justify-center">
                  <Skeleton className="h-5 w-5 rounded-full" />
                </div>
              </TableCell>
              <TableCell>
                <div className="flex justify-center">
                  <Skeleton className="h-5 w-5 rounded-full" />
                </div>
              </TableCell>
            </TableRow>
          ))}
          {/* Certificate previews row */}
          <TableRow>
            <TableCell />
            <TableCell className="align-top">
              <div className="flex flex-col items-center gap-2 py-4">
                <Skeleton className="h-[165px] w-[124px]" />
              </div>
            </TableCell>
            <TableCell className="align-top">
              <div className="flex flex-col items-center gap-2 py-4">
                <Skeleton className="h-[165px] w-[124px]" />
                <div className="space-y-2 w-full max-w-44">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              </div>
            </TableCell>
            <TableCell className="align-top">
              <div className="flex flex-col items-center gap-2 py-4">
                <Skeleton className="h-[165px] w-[124px]" />
                <div className="space-y-2 w-full max-w-44">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              </div>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

const CertificationTable = withSuspense(() => {
  const {
    softwareEscrow: {
      id: softwareEscrowId,
      status,
      certificationCertified,
      certificationVerified,
      requiredContentTypes,
      activatedAt,
      policy,
    },
  } = useSoftwareEscrow();
  const {
    data: {
      assets: { nodesCount: unlabeledNodesCount },
    },
  } = useSuspenseQuery(AssetsDocument, {
    variables: {
      softwareEscrowId,
      labeled: false,
    },
  });
  const {
    data: {
      assets: { nodesCount: sourceCodeNodesCount },
    },
  } = useSuspenseQuery(AssetsDocument, {
    variables: {
      softwareEscrowId,
      contentType: AssetContentTypeEnum.SourceCode,
    },
  });
  const {
    data: {
      assets: { nodesCount: documentationNodesCount },
    },
  } = useSuspenseQuery(AssetsDocument, {
    variables: {
      softwareEscrowId,
      contentType: AssetContentTypeEnum.Documentation,
    },
  });
  const {
    data: {
      assets: { nodesCount: dataNodesCount },
    },
  } = useSuspenseQuery(AssetsDocument, {
    variables: {
      softwareEscrowId,
      contentType: AssetContentTypeEnum.Data,
    },
  });

  const assetConfig = useMemo(
    () => ({
      unlabeled: unlabeledNodesCount,
      [AssetContentTypeEnum.SourceCode]: sourceCodeNodesCount,
      [AssetContentTypeEnum.Documentation]: documentationNodesCount,
      [AssetContentTypeEnum.Data]: dataNodesCount,
    }),
    [sourceCodeNodesCount, documentationNodesCount, dataNodesCount, unlabeledNodesCount],
  );

  const data = useMemo(
    () =>
      Object.entries(assetConfig)
        .filter(([contentType, nodesCount]) => !(contentType === 'unlabeled' && !nodesCount))
        .map(([contentType, nodesCount]) => {
          const isMarkedAsRequired =
            contentType === 'unlabeled' || (requiredContentTypes as readonly string[]).includes(contentType);

          return {
            id: contentType as AssetContentTypeEnum,
            contentType: contentTypeMap[contentType as keyof typeof contentTypeMap],
            validated: {
              isMarkedAsRequired,
              isEnabled: Boolean(activatedAt && nodesCount),
              isEmpty: !nodesCount,
            },
            verified: {
              isMarkedAsRequired,
              isEnabled: certificationVerified && Boolean(nodesCount),
              isEmpty: !nodesCount,
            },
            certified: {
              isMarkedAsRequired,
              isEnabled: certificationCertified && Boolean(nodesCount),
              isEmpty: !nodesCount,
            },
          };
        }),
    [assetConfig, activatedAt, certificationVerified, certificationCertified, requiredContentTypes],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const isTerminated = status === EscrowStatusEnum.Terminated;

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={cn(
                    cell.column.id === 'contentType' && 'font-semibold',
                    isTerminated && cell.column.id !== 'contentType' && 'opacity-30',
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
          <TableRow className="hover:bg-white">
            <TableCell />
            <TableCell className="align-top py-4 px-6">
              <div className="space-y-8 max-w-44 mx-auto">
                <div className="p-6 text-center">
                  <CertificatePreview type="validated" isTerminated={isTerminated} isEnabled={Boolean(activatedAt)} />
                </div>
                {Boolean(activatedAt) && (
                  <CertificateContainer
                    type="validated"
                    dialogTrigger={
                      <Button className="w-full" size="sm">
                        View certificate
                      </Button>
                    }
                    canDownload={policy.read}
                  />
                )}
              </div>
            </TableCell>
            <TableCell className="align-top py-4 px-6">
              <VerifiedCell />
            </TableCell>
            <TableCell className="align-top py-4 px-6">
              <CertifiedCell />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}, CertificationTableSkeleton);

const CertificationContainer = ({ ref }: { ref: React.RefObject<HTMLDivElement | null> }) => {
  const {
    softwareEscrow: { activatedAt },
  } = useSoftwareEscrow();

  if (!activatedAt)
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Verification certificates</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyBlock
            icon={Inbox}
            title="No certificates available yet"
            description="You’ll see your corresponding certificates once your escrow becomes active."
            withCard={false}
            classes={{ content: 'flex flex-col justify-center h-[473px]', title: 'text-base' }}
          />
        </CardContent>
      </Card>
    );

  return (
    <Card ref={ref}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Verification certificates</CardTitle>
      </CardHeader>
      <CardContent>
        <CertificationTable />
      </CardContent>
    </Card>
  );
};

export default CertificationContainer;
